import type { FileUIPart } from 'ai';

import type { IDocumentReaderUploadReference } from '@commenda/api/commenda-ai/document-reader/types';
import type { CommendaAiPageContext } from '@commenda/api/commenda-ai/types';
import type { useCommendaAiChat } from '@commenda/api/commenda-ai/useCommendaAiChat';
import type { AgentAttachment } from '@commenda/types';

/** The first message a new chat stashes for its conversation page to send. */
export interface IChatPendingMessage {
  text: string;
  attachments: AgentAttachment[];
  dataRoom: IDocumentReaderUploadReference | null;
}

export interface IChatTurnParams {
  text: string;
  files: FileUIPart[];
  dataRoom: IDocumentReaderUploadReference | null;
  pageContext: CommendaAiPageContext | null;
}

type TChatTurn = Parameters<
  ReturnType<typeof useCommendaAiChat>['sendMessage']
>;

/** How a data room rides a chat send, typed in the conversation or stashed by a new chat for its first send. */
export class DataRoomSendHelper {
  /** Without a room the stash is the same JSON as before the reader. */
  static toPendingMessage(
    text: string,
    attachments: readonly AgentAttachment[],
    dataRoom: IDocumentReaderUploadReference | null
  ): string {
    return JSON.stringify({
      text,
      attachments,
      ...(dataRoom ? { dataRoom } : {}),
    });
  }

  /** A stash that is not a JSON object is the older plain-text form, sent as the text. */
  static parsePendingMessage(raw: string): IChatPendingMessage {
    const plain = { text: raw, attachments: [], dataRoom: null };
    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null) return plain;
      // Safe: a non-null object, and every field below is type-checked before use.
      const { text, attachments, dataRoom } = parsed as Record<string, unknown>;
      return {
        text: typeof text === 'string' ? text : '',
        // Written by this helper's caller from the composer's own attachments.
        attachments: Array.isArray(attachments)
          ? (attachments as AgentAttachment[])
          : [],
        dataRoom: DataRoomSendHelper.isUploadReference(dataRoom)
          ? dataRoom
          : null,
      };
    } catch {
      return plain;
    }
  }

  /** A room rides as its id in the body (the transport merges it) and as the data part the backend
   * stores, so its pill shows before a reload. Without one the send is today's. */
  static toTurn({
    text,
    files,
    dataRoom,
    pageContext,
  }: Readonly<IChatTurnParams>): TChatTurn {
    if (!dataRoom)
      return [
        { text, files },
        pageContext ? { body: { pageContext } } : undefined,
      ];
    return [
      {
        parts: [
          { type: 'text', text },
          ...files,
          { type: 'data-document-upload', data: dataRoom },
        ],
      },
      {
        body: {
          ...(pageContext ? { pageContext } : {}),
          documentUploadId: dataRoom.uploadId,
        },
      },
    ];
  }

  private static isUploadReference(
    value: unknown
  ): value is IDocumentReaderUploadReference {
    if (typeof value !== 'object' || value === null) return false;
    // Safe: a non-null object, and each field is type-checked here.
    const { uploadId, fileCount, totalSizeBytes } = value as Record<
      string,
      unknown
    >;
    return (
      typeof uploadId === 'string' &&
      typeof fileCount === 'number' &&
      typeof totalSizeBytes === 'number'
    );
  }
}
